import { FunctionComponent, ReactElement } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';

import Callout from '../../../../../components/_core/Callout';
import {
  BeneficiarySchema,
  BeneficiarySchemaWithEmail,
  beneficiarySchemaWithEmail,
} from '../beneficiarySchema';
import type { CurrentBeneficiariesEmails } from '../helpers';
import locales from '../locales';
import BeneficiaryFormWrapper from './BeneficiaryFormWrapper';
import EmailField from './EmailField';
import RequiredFields from './RequiredFields';

interface BeneficiaryFormProps {
  beneficiary: DefaultValues<BeneficiarySchemaWithEmail>;
  currentBeneficiariesEmails: CurrentBeneficiariesEmails;
  primaryAction: {
    title: string;
    onSubmit: (formValues: BeneficiarySchema) => void;
    isSending: boolean;
    isDisabled?: boolean;
  };
  secondaryAction: ReactElement;
  roleWarningCalloutContext?: 'create' | 'edit';
}

const BeneficiaryFormWithEmail: FunctionComponent<BeneficiaryFormProps> = ({
  beneficiary,
  currentBeneficiariesEmails,
  primaryAction,
  roleWarningCalloutContext = 'edit',
  secondaryAction,
}) => {
  const form = useForm<BeneficiarySchemaWithEmail>({
    defaultValues: beneficiary,
    resolver: zodResolver(
      beneficiarySchemaWithEmail({
        currentBeneficiariesEmails,
        initialEmailValue: beneficiary.inviteeEmail ?? '',
      }),
    ),
  });

  return (
    <FormProvider {...form}>
      <BeneficiaryFormWrapper
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {({ isInvalidBeneficiary, isValidBeneficiary }) => (
          <>
            <RequiredFields
              isInvalidBeneficiary={isInvalidBeneficiary}
              roleWarningCalloutContext={roleWarningCalloutContext}
            />
            {isValidBeneficiary ? (
              <>
                <EmailField />
                <Callout hideIcon>{locales.validBeneficiary}</Callout>
              </>
            ) : null}
          </>
        )}
      </BeneficiaryFormWrapper>
    </FormProvider>
  );
};

export default BeneficiaryFormWithEmail;
